import React, { useCallback, useEffect, useState } from 'react';
import { formatCostUsd } from '../../utils/formatCost';
import { enrichSessionWithCost } from '../../utils/sessionCostEstimate';
import { getApiBase } from '../../lib/apiBase';
import { getDashboardAuthHeaders } from '../../lib/dashboardSession';
import MetricSmallBox from './MetricSmallBox';
import SessionTable from './SessionTable';
import { formatDuration } from './formatters';

const RECENT_LIMIT = 10;

export default function DashboardHomeView({
  onError,
  refreshToken,
  sessionMessages,
  setSessionMessages,
  expandedId,
  setExpandedId,
  loadingMessagesId,
  setLoadingMessagesId,
}) {
  const [metrics, setMetrics] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCostMonth, setSelectedCostMonth] = useState('');

  const base = getApiBase();

  const loadSessionMessages = useCallback(
    async (sessionId) => {
      if (sessionMessages[sessionId]) return sessionMessages[sessionId];
      setLoadingMessagesId(sessionId);
      try {
        const res = await fetch(
          `${base}/api/dashboard/session-messages?sessionId=${encodeURIComponent(sessionId)}`,
          { headers: getDashboardAuthHeaders() }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
        const msgs = Array.isArray(data.messages) ? data.messages : [];
        setSessionMessages((prev) => ({ ...prev, [sessionId]: msgs }));
        return msgs;
      } finally {
        setLoadingMessagesId(null);
      }
    },
    [base, sessionMessages, setLoadingMessagesId, setSessionMessages]
  );

  const toggleSession = async (sessionId, isOpen) => {
    if (isOpen) {
      setExpandedId(null);
      return;
    }
    setExpandedId(sessionId);
    if (!sessionMessages[sessionId]) {
      try {
        await loadSessionMessages(sessionId);
      } catch (err) {
        onError(err?.message || 'Failed to load messages');
        setExpandedId(null);
      }
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    onError('');
    try {
      const headers = getDashboardAuthHeaders();
      const [metricsRes, sessionsRes] = await Promise.all([
        fetch(`${base}/api/dashboard/metrics`, { headers }),
        fetch(`${base}/api/dashboard/sessions?limit=${RECENT_LIMIT}`, { headers }),
      ]);
      const metricsData = await metricsRes.json().catch(() => ({}));
      const sessionsData = await sessionsRes.json().catch(() => ({}));
      if (!metricsRes.ok) throw new Error(metricsData.error || `Metrics error ${metricsRes.status}`);
      if (!sessionsRes.ok) throw new Error(sessionsData.error || `Sessions error ${sessionsRes.status}`);
      setMetrics(metricsData.metrics || {});
      const list = Array.isArray(sessionsData.sessions) ? sessionsData.sessions : [];
      setRecentSessions(list.map((s) => enrichSessionWithCost(s)));
    } catch (err) {
      const msg = err?.message || 'Failed to load dashboard';
      onError(
        msg.includes('fetch failed') || msg.includes('Failed to fetch')
          ? 'Backend not reachable. Run npm run dev, then refresh.'
          : msg
      );
    } finally {
      setLoading(false);
    }
  }, [base, onError]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshToken]);

  useEffect(() => {
    const months = Array.isArray(metrics?.monthlyCostBreakdown)
      ? metrics.monthlyCostBreakdown
      : [];
    if (!months.length) {
      setSelectedCostMonth('');
      return;
    }
    const hasSelected = months.some((m) => m.key === selectedCostMonth);
    if (!hasSelected) {
      setSelectedCostMonth(metrics.currentMonthKey || months[months.length - 1].key);
    }
  }, [metrics, selectedCostMonth]);

  const monthlyCostBreakdown = Array.isArray(metrics?.monthlyCostBreakdown)
    ? metrics.monthlyCostBreakdown
    : [];
  const selectedMonthCost =
    monthlyCostBreakdown.find((m) => m.key === selectedCostMonth) ||
    monthlyCostBreakdown.find((m) => m.key === metrics?.currentMonthKey) ||
    monthlyCostBreakdown[monthlyCostBreakdown.length - 1] ||
    null;
  const overallCostUsd = metrics
    ? metrics.overallCostUsd ?? (Number(metrics.totalCostUsd) || 0) + (Number(metrics.avatarCostToDateUsd) || 0)
    : 0;

  const cards = metrics
    ? [
        {
          icon: 'fas fa-users',
          label: 'Total sessions',
          value: metrics.totalSessions,
          sub: `${metrics.completedSessions} completed · ${metrics.inProgressSessions} in progress`,
        },
        {
          icon: 'fas fa-comments',
          label: 'Total messages',
          value: metrics.totalMessages,
          sub: 'Across all sessions',
        },
        {
          icon: 'fas fa-dollar-sign',
          label: 'Total session cost',
          value: formatCostUsd(metrics.totalCostUsd),
          sub: `Avg ${formatCostUsd(metrics.avgCostPerSession)} per session (estimated)`,
        },
        {
          icon: 'fas fa-chart-bar',
          label: 'Avg messages / session',
          value: metrics.avgMessagesPerSession,
        },
        {
          icon: 'fas fa-clock',
          label: 'Avg session duration',
          value: formatDuration(metrics.avgDurationSeconds),
        },
        {
          icon: 'fas fa-calendar-day',
          label: 'Sessions today',
          value: metrics.sessionsToday,
        },
        {
          icon: 'fas fa-check-circle',
          label: 'Sessions this week',
          value: metrics.sessionsThisWeek,
        },
        {
          icon: 'fas fa-receipt',
          label: 'Overall cost',
          value: formatCostUsd(overallCostUsd),
          sub: `Includes ${formatCostUsd(metrics.avatarCostToDateUsd)} avatar fees at ${formatCostUsd(metrics.avatarMonthlyCostUsd)}/mo`,
        },
      ]
    : [];

  return (
    <>
      <div className="row">
        {cards.map((c, i) => (
          <MetricSmallBox key={c.label} {...c} index={i} />
        ))}
      </div>

      {metrics && (
        <div className="card dashboard-cost-breakdown">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-file-invoice-dollar mr-1" />
              Cost breakdown
            </h3>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <div className="info-box bg-light cost-summary-box">
                  <span className="info-box-icon bg-success">
                    <i className="fas fa-dollar-sign" />
                  </span>
                  <div className="info-box-content">
                    <span className="info-box-text">All session cost</span>
                    <span className="info-box-number">{formatCostUsd(metrics.totalCostUsd)}</span>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="info-box bg-light cost-summary-box">
                  <span className="info-box-icon bg-primary">
                    <i className="fas fa-user-clock" />
                  </span>
                  <div className="info-box-content">
                    <span className="info-box-text">Avatar monthly cost</span>
                    <span className="info-box-number">
                      {formatCostUsd(metrics.avatarCostToDateUsd)}
                    </span>
                    <span className="info-box-text">
                      {metrics.avatarMonthsToDate || 0} month(s) x{' '}
                      {formatCostUsd(metrics.avatarMonthlyCostUsd)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="info-box bg-light cost-summary-box">
                  <span className="info-box-icon bg-warning">
                    <i className="fas fa-calculator" />
                  </span>
                  <div className="info-box-content">
                    <span className="info-box-text">Overall cost</span>
                    <span className="info-box-number">{formatCostUsd(overallCostUsd)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group mb-3">
              <label htmlFor="cost-month-select">Month and year</label>
              <select
                id="cost-month-select"
                className="form-control"
                value={selectedMonthCost?.key || ''}
                onChange={(e) => setSelectedCostMonth(e.target.value)}
              >
                {monthlyCostBreakdown.map((month) => (
                  <option key={month.key} value={month.key}>
                    {month.label} - {formatCostUsd(month.totalCostUsd)}
                  </option>
                ))}
              </select>
            </div>

            {selectedMonthCost && (
              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-0">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Sessions</th>
                      <th>Session cost</th>
                      <th>Avatar cost</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{selectedMonthCost.label}</td>
                      <td>{selectedMonthCost.sessions}</td>
                      <td>{formatCostUsd(selectedMonthCost.sessionCostUsd)}</td>
                      <td>
                        {formatCostUsd(selectedMonthCost.avatarMonthlyCostUsd)}
                        {selectedMonthCost.isFuture ? (
                          <span className="text-muted ml-1">(not incurred yet)</span>
                        ) : null}
                      </td>
                      <td className="font-weight-bold">
                        {formatCostUsd(selectedMonthCost.totalCostUsd)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <i className="fas fa-history mr-1" />
            Recent sessions
          </h3>
          <div className="card-tools">
            <a href="/conversations" className="btn btn-tool btn-sm text-primary">
              View all <i className="fas fa-arrow-right ml-1" />
            </a>
          </div>
        </div>
        <div className="card-body p-0">
          <SessionTable
            sessions={recentSessions}
            expandedId={expandedId}
            onToggle={toggleSession}
            sessionMessages={sessionMessages}
            loadingMessagesId={loadingMessagesId}
            loading={loading}
            emptyMessage="No conversations yet. Complete a session on the avatar page to save one."
          />
        </div>
      </div>
    </>
  );
}
